import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SignOut, User, Crown, ShieldCheck, Translate } from "@phosphor-icons/react";
import { NotificationBell } from "@/components/NotificationPanel";
import NotificationPanel from "@/components/NotificationPanel";
import PushToggle from "@/components/PushToggle";
import { getNavItems } from "@/lib/roleNav";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANGS = [
  { code: "pt", label: "PT", flag: "🇵🇹" },
  { code: "en", label: "EN", flag: "🇬🇧" },
  { code: "es", label: "ES", flag: "🇪🇸" },
];

function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = LANGS.find(l => i18n.language?.startsWith(l.code)) || LANGS[0];
  return (
    <div style={{ position: "relative" }} data-testid="lang-switcher">
      <button
        data-testid="lang-toggle"
        onClick={() => setOpen(o => !o)}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 9999, border: "1px solid var(--border-color)", background: "var(--bg)", cursor: "pointer", fontSize: "0.78rem", fontWeight: 600, color: "var(--text-main)" }}
      >
        <Translate size={14} weight="duotone" />
        <span>{current.flag} {current.label}</span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, background: "var(--surface)", border: "1px solid var(--border-color)", borderRadius: 12, boxShadow: "0 12px 30px -10px rgba(0,0,0,0.18)", overflow: "hidden", zIndex: 100, minWidth: 130 }}>
          {LANGS.map(l => (
            <button
              key={l.code}
              data-testid={`lang-${l.code}`}
              onClick={() => { i18n.changeLanguage(l.code); setOpen(false); }}
              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 14px", border: "none", cursor: "pointer", fontFamily: "Manrope", fontSize: "0.8rem", background: i18n.language?.startsWith(l.code) ? "rgba(45,74,62,0.08)" : "transparent", color: "var(--text-main)", fontWeight: i18n.language?.startsWith(l.code) ? 700 : 500, textAlign: "left" }}
            >
              <span>{l.flag}</span> <span>{l.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navigation({ currentView, setCurrentView, user, demoMode, onLogout }) {
  const { t } = useTranslation();
  const [showNotifications, setShowNotifications] = useState(false);
  const [chatUnreadTotal, setChatUnreadTotal] = useState(0);

  const userRole = user?.role || "user";

  // Poll unread chat counts every 30s when logged in (lightweight)
  useEffect(() => {
    if (!user) { setChatUnreadTotal(0); return; }
    let cancelled = false;
    const fetchCounts = async () => {
      const token = localStorage.getItem("blumove_token");
      if (!token) return;
      try {
        const res = await axios.get(`${API}/chat/unread-counts`,
          { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setChatUnreadTotal(res.data?.total || 0);
      } catch (e) { /* ignore */ }
    };
    fetchCounts();
    const id = setInterval(fetchCounts, 30000);
    // Refresh on view change to "my-shipments" (which marks-read on expand)
    const onFocus = () => fetchCounts();
    window.addEventListener("focus", onFocus);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [user, currentView]);

  // Single source of truth lives in /lib/roleNav.js — both Navigation and
  // LandingPage call getNavItems(role) so menus never drift.
  const items = getNavItems(user ? userRole : null).map((it) => ({
    id: it.id,
    label: it.id === "admin" && userRole === "super_admin" ? "Super Admin" : t(it.labelKey),
    icon: it.icon,
  }));

  return (
    <>
      <header className="glass-header fixed top-0 left-0 right-0 z-50" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <button
            data-testid="nav-logo"
            onClick={() => setCurrentView("landing")}
            className="flex items-center gap-2"
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            <img src="/assets/logo.png" alt="Bluemove" style={{ height: 32 }} />
          </button>
          <nav className="flex items-center gap-1">
            {items.map(item => {
              const showDot = item.id === "my-shipments" && chatUnreadTotal > 0;
              return (
                <button
                  key={item.id}
                  data-testid={`nav-${item.id}`}
                  className={`nav-item flex items-center gap-2 ${currentView === item.id ? "active" : ""}`}
                  onClick={() => setCurrentView(item.id)}
                  style={{ border: "none", fontFamily: "Manrope", position: "relative" }}
                >
                  <item.icon size={16} weight={currentView === item.id ? "fill" : "regular"} />
                  <span className="hidden lg:inline">{item.label}</span>
                  {showDot && (
                    <span
                      data-testid="nav-chat-unread-dot"
                      title={`${chatUnreadTotal} mensagem(ns) por ler`}
                      style={{
                        position: "absolute", top: 4, right: 4,
                        minWidth: 16, height: 16, padding: "0 4px",
                        borderRadius: 9999, background: "#dc2626", color: "white",
                        fontSize: "0.6rem", fontWeight: 700,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        border: "2px solid var(--surface)",
                        boxShadow: "0 0 0 2px rgba(220,38,38,0.15)",
                      }}
                    >
                      {chatUnreadTotal > 9 ? "9+" : chatUnreadTotal}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
          <div className="flex items-center gap-2">
            <PushToggle compact />
            <LanguageSwitcher />
            <NotificationBell onClick={() => setShowNotifications(true)} />
            {user && (
              <div className="hidden sm:flex items-center gap-2" style={{ padding: "4px 12px", borderRadius: 9999, background: user.role === "super_admin" ? "rgba(220,38,38,0.08)" : "var(--bg)", fontSize: "0.8rem", border: user.role === "super_admin" ? "1px solid rgba(220,38,38,0.3)" : "none" }}>
                {user.role === "super_admin"
                  ? <Crown size={14} weight="fill" style={{ color: "#dc2626" }} />
                  : <User size={14} weight="duotone" style={{ color: "var(--primary)" }} />
                }
                <span style={{ fontWeight: 600, color: user.role === "super_admin" ? "#dc2626" : "var(--text-main)" }}>{user.name}</span>
                <span style={{ color: user.role === "super_admin" ? "#dc2626" : "var(--text-muted)", fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {user.role === "super_admin" ? "SUPER ADMIN" : user.role}
                </span>
              </div>
            )}
            {demoMode && (
              <span style={{ padding: "4px 10px", borderRadius: 9999, background: "rgba(232,163,89,0.1)", color: "var(--warning)", fontSize: "0.7rem", fontWeight: 600 }}>DEMO</span>
            )}
            {user && (
              <button
                data-testid="logout-btn"
                onClick={onLogout}
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", display: "flex", alignItems: "center", padding: 6, borderRadius: 8 }}
                onMouseEnter={e => e.currentTarget.style.color = "var(--error)"}
                onMouseLeave={e => e.currentTarget.style.color = "var(--text-muted)"}
              >
                <SignOut size={18} weight="bold" />
              </button>
            )}
          </div>
        </div>
      </header>
      <NotificationPanel isOpen={showNotifications} onClose={() => setShowNotifications(false)} />
    </>
  );
}
